import { useState, useEffect } from 'react';

const STORAGE_KEY = 'energie-monitor-data';

const initialData = {
  companyName: '',
  objects: [
    {
      id: 'obj-1',
      name: 'Wohnung',
      meters: [
        { id: 'meter-1', name: 'Strom', type: 'electricity', readings: [], params: {} },
        { id: 'meter-2', name: 'Gas', type: 'gas', readings: [], params: { zNumber: 1.0, heatingValue: 10.0 } },
        { id: 'meter-3', name: 'Wasser', type: 'water', readings: [], params: {} }
      ]
    }
  ],
  stats: {
    totalReadings: 0,
    analyses: 0,
    magicDownloads: 0,
    adClicks: 0,
    shares: 0
  }
};

export const useEnergyData = () => {
  const [data, setData] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : initialData;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const setCompanyName = (name) => {
    setData(prev => ({
      ...prev,
      companyName: name
    }));
  };

  const addObject = (name) => {
    const newObj = {
      id: `obj-${Date.now()}`,
      name,
      meters: []
    };
    setData(prev => ({
      ...prev,
      objects: [...prev.objects, newObj]
    }));
  };

  const updateObject = (objId, updates) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId ? { ...obj, ...updates } : obj
      )
    }));
  };

  const removeObject = (objId) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.filter(o => o.id !== objId)
    }));
  };

  const addMeter = (objId, meterData) => {
    const newMeter = {
      id: `meter-${Date.now()}`,
      ...meterData,
      readings: []
    };
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? { ...obj, meters: [...obj.meters, newMeter] }
          : obj
      )
    }));
  };

  const updateMeter = (objId, meterId, updates) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? {
              ...obj,
              meters: obj.meters.map(m =>
                m.id === meterId ? { ...m, ...updates } : m
              )
            }
          : obj
      )
    }));
  };

  const deleteMeter = (objId, meterId) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? { ...obj, meters: obj.meters.filter(m => m.id !== meterId) }
          : obj
      )
    }));
  };

  const addReading = (objId, meterId, reading) => {
    const newReading = {
      id: `reading-${Date.now()}-${Math.random()}`,
      ...reading,
      timestamp: reading.timestamp || new Date().toISOString()
    };
    
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? {
              ...obj,
              meters: obj.meters.map(m =>
                m.id === meterId
                  ? { ...m, readings: [...(m.readings || []), newReading] }
                  : m
              )
            }
          : obj
      ),
      stats: {
        ...prev.stats,
        totalReadings: prev.stats.totalReadings + 1
      }
    }));
  };

  const updateReading = (objId, meterId, readingId, updates) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? {
              ...obj,
              meters: obj.meters.map(m =>
                m.id === meterId
                  ? {
                      ...m,
                      readings: m.readings.map(r =>
                        r.id === readingId ? { ...r, ...updates } : r
                      )
                    }
                  : m
              )
            }
          : obj
      )
    }));
  };

  const deleteReading = (objId, meterId, readingId) => {
    setData(prev => ({
      ...prev,
      objects: prev.objects.map(obj =>
        obj.id === objId
          ? {
              ...obj,
              meters: obj.meters.map(m =>
                m.id === meterId
                  ? { ...m, readings: m.readings.filter(r => r.id !== readingId) }
                  : m
              )
            }
          : obj
      )
    }));
  };

  const duplicateReading = (objId, meterId, readingId) => {
    setData(prev => {
      const obj = prev.objects.find(o => o.id === objId);
      const meter = obj?.meters.find(m => m.id === meterId);
      const reading = meter?.readings.find(r => r.id === readingId);
      
      if (!reading) return prev;
      
      const newReading = {
        ...reading,
        id: `reading-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString()
      };
      
      return {
        ...prev,
        objects: prev.objects.map(o =>
          o.id === objId
            ? {
                ...o,
                meters: o.meters.map(m =>
                  m.id === meterId
                    ? { ...m, readings: [...m.readings, newReading] }
                    : m
                )
              }
            : o
        )
      };
    });
  };

  const incrementStat = (statName) => {
    setData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [statName]: (prev.stats[statName] || 0) + 1
      }
    }));
  };

  const importData = (newData) => {
    setData(newData);
  };

  const clearAllData = () => {
    setData(initialData);
  };

  return {
    data,
    setCompanyName,
    addObject,
    updateObject,
    removeObject,
    addMeter,
    updateMeter,
    deleteMeter,
    addReading,
    updateReading,
    deleteReading,
    duplicateReading,
    incrementStat,
    importData,
    clearAllData
  };
};